'use client';

import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { sanitizeMediaUrl } from '../url-safety';
import { useEditMode, type ImageValue } from './context';
import { getByPath } from './get-by-path';

type Props = {
  path: string;
  value: ImageValue | undefined;
  className?: string;
  style?: CSSProperties;
  emptyLabel?: string;
};

function clampImagePosition(value: unknown): number {
  return Math.min(
    100,
    Math.max(
      0,
      typeof value === 'number' && Number.isFinite(value)
        ? Math.round(value)
        : 50,
    ),
  );
}

function normalizeDraft(value: ImageValue | undefined): ImageValue | undefined {
  if (!value) return undefined;
  return {
    ...value,
    positionX: clampImagePosition(value.positionX),
    positionY: clampImagePosition(value.positionY),
  };
}

function getImageObjectPosition(value: ImageValue | undefined): string | undefined {
  const x = typeof value?.positionX === 'number' ? value.positionX : undefined;
  const y = typeof value?.positionY === 'number' ? value.positionY : undefined;
  if (x === undefined && y === undefined) return undefined;
  return `${x ?? 50}% ${y ?? 50}%`;
}

export function EditableImage({
  path,
  value,
  className,
  style,
  emptyLabel = 'Adicionar imagem',
}: Props) {
  const {
    editing,
    values,
    selectedPath,
    setSelected,
    setByPath,
    renderImagePicker,
  } = useEditMode();
  const [open, setOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [draft, setDraft] = useState<ImageValue | undefined>(undefined);
  const draftRef = useRef<ImageValue | undefined>(undefined);
  const frameRef = useRef<HTMLDivElement | null>(null);

  // Live content (Convex draft/published values) wins; the `value` prop is the
  // client's static default/fallback.
  const resolved = getByPath(values, path) as ImageValue | undefined;
  const effective = resolved ?? value;
  const isSelected = selectedPath === path && open;
  const draftValue = isSelected ? (draft ?? normalizeDraft(effective)) : effective;

  const src = sanitizeMediaUrl(draftValue?.url) ?? null;
  const alt = draftValue?.alt ?? '';
  const objectPosition = getImageObjectPosition(draftValue);
  const imageStyle = objectPosition
    ? { ...style, objectPosition }
    : style;

  const ringClass = isSelected
    ? 'ring-2 ring-[#d8ad63] ring-offset-2 ring-offset-[#fffaf0]'
    : 'hover:ring-2 hover:ring-[#d8ad63]/60 hover:ring-offset-2 hover:ring-offset-[#fffaf0]';

  // The wrapper needs a positioning context so the selection overlay anchors
  // to it. Only force `relative` when the consumer hasn't already set a
  // position (e.g. a full-bleed `absolute inset-0` background) -otherwise the
  // appended `relative` would override their `absolute` and break the layout.
  const hasPositionClass = (className ?? '')
    .split(/\s+/)
    .some(
      (c) =>
        c === 'absolute' ||
        c === 'fixed' ||
        c === 'sticky' ||
        c === 'relative',
    );
  const wrapperPositionClass = hasPositionClass ? '' : 'relative';

  useEffect(() => {
    if (selectedPath !== path && open) {
      setOpen(false);
      setLibraryOpen(false);
      draftRef.current = undefined;
      setDraft(undefined);
    }
  }, [open, path, selectedPath]);

  if (!editing) {
    if (!src) return null;
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={src} alt={alt} className={className} style={imageStyle} />;
  }

  function openPicker(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const nextDraft = normalizeDraft(effective);
    setSelected(path);
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    setOpen(true);
  }

  function openLibrary(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const nextDraft = normalizeDraft(effective);
    setSelected(path);
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    setOpen(true);
    setLibraryOpen(true);
  }

  function closeEditor() {
    draftRef.current = undefined;
    setDraft(undefined);
    setOpen(false);
    setLibraryOpen(false);
    setSelected(null);
  }

  function updateDraft(next: ImageValue | undefined) {
    const normalized = normalizeDraft(next);
    draftRef.current = normalized;
    setDraft(normalized);
  }

  function persistDraft(next: ImageValue | undefined) {
    const normalized = normalizeDraft(next);
    draftRef.current = normalized;
    setDraft(normalized);
    setByPath(path, normalized);
  }

  function updateDraftPosition(e: PointerEvent) {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    const nextX = clampImagePosition(((e.clientX - rect.left) / rect.width) * 100);
    const nextY = clampImagePosition(((e.clientY - rect.top) / rect.height) * 100);
    persistDraft({
      ...(draft ?? effective ?? {}),
      positionX: nextX,
      positionY: nextY,
    });
  }

  function startReposition(e: PointerEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    updateDraftPosition(e);
  }

  function moveReposition(e: PointerEvent<HTMLButtonElement>) {
    if (e.buttons !== 1) return;
    updateDraftPosition(e);
  }

  function saveDraft(next: ImageValue | undefined = draftRef.current ?? draftValue) {
    persistDraft(next);
    closeEditor();
  }

  function removeImage() {
    setByPath(path, undefined);
    closeEditor();
  }

  function closeLibrary() {
    setLibraryOpen(false);
  }

  if (!src) {
    return (
      <Fragment>
        <button
          type="button"
          onClick={openLibrary}
          className={`${className ?? ''} flex min-h-[8rem] items-center justify-center rounded-xl border-2 border-dashed border-[#d8ad63]/55 bg-[#fffaf0]/85 text-sm font-medium text-[#7b633f] shadow-[0_18px_44px_-36px_rgba(52,38,20,0.55)] hover:bg-[#fff5e7]`}
          style={imageStyle}
        >
          {emptyLabel}
        </button>
        {libraryOpen &&
          renderImagePicker &&
          renderImagePicker({
            path,
            mode: 'library',
            currentValue: effective,
            draftValue,
            onDraftChange: updateDraft,
            onPersist: persistDraft,
            onCommit: saveDraft,
            onRemove: removeImage,
            onClose: closeLibrary,
          })}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div
        ref={frameRef}
        className={`${className ?? ''} ${wrapperPositionClass} ${ringClass}`}
        style={style}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={alt}
          className="h-full w-full cursor-pointer object-cover"
          style={imageStyle}
          onClick={openPicker}
        />
        {isSelected && (
          <Fragment>
            <div
              className="pointer-events-auto absolute left-3 top-3 z-50 flex max-w-[calc(100%-1.5rem)] items-center overflow-hidden rounded-full border border-white/20 bg-[#17130f]/88 text-[11px] font-medium text-white shadow-[0_16px_34px_-22px_rgba(23,19,15,0.85)] backdrop-blur"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={openLibrary}
                className="whitespace-nowrap px-3 py-2 hover:bg-white/12"
              >
                Editar imagem
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  persistDraft({
                    ...(draft ?? effective ?? {}),
                    positionX: 50,
                    positionY: 50,
                  });
                }}
                className="border-l border-white/15 px-3 py-2 hover:bg-white/12"
              >
                Cortar
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="border-l border-white/15 px-3 py-2 hover:bg-white/12"
              >
                Mover
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeImage();
                }}
                className="border-l border-white/15 px-3 py-2 text-[#ffd0c9] hover:bg-[#7f1d1d]/55"
              >
                Apagar
              </button>
            </div>
            <div className="pointer-events-none absolute inset-0 z-20">
              <button
                type="button"
                aria-label="Reposicionar imagem"
                onPointerDown={startReposition}
                onPointerMove={moveReposition}
                className="pointer-events-auto absolute inset-[8%] cursor-move rounded-[inherit] border border-white/90 bg-transparent shadow-[0_0_0_9999px_rgba(15,23,42,0.18)]"
              >
                {[
                  'left-0 top-0',
                  'left-1/2 top-0',
                  'right-0 top-0',
                  'right-0 top-1/2',
                  'right-0 bottom-0',
                  'left-1/2 bottom-0',
                  'left-0 bottom-0',
                  'left-0 top-1/2',
                ].map((position) => (
                  <span
                    key={position}
                    className={`absolute h-3 w-3 rounded-full border border-[#d8ad63] bg-[#fffaf0] shadow ${position} -translate-x-1/2 -translate-y-1/2`}
                  />
                ))}
              </button>
            </div>
          </Fragment>
        )}
      </div>
      {open &&
        renderImagePicker &&
        renderImagePicker({
          path,
          mode: 'inline',
          currentValue: effective,
          draftValue,
          onDraftChange: updateDraft,
          onPersist: persistDraft,
          onCommit: saveDraft,
          onRemove: removeImage,
          onClose: closeEditor,
        })}
      {libraryOpen &&
        renderImagePicker &&
        renderImagePicker({
          path,
          mode: 'library',
          currentValue: effective,
          draftValue,
          onDraftChange: updateDraft,
          onPersist: persistDraft,
          onCommit: saveDraft,
          onRemove: removeImage,
          onClose: closeLibrary,
        })}
    </Fragment>
  );
}
